import React from 'react';
import { motion } from 'framer-motion';
import { FaUniversity, FaGraduationCap, FaGlobe, FaRoute, FaUserGraduate } from 'react-icons/fa';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5 }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { 
    opacity: 1,
    transition: { duration: 0.8 }
  }
};

const UniversityProgression = () => {
  const partnerUniversities = [
    {
      name: 'BPP University',
      description: 'Specializes in professional education, offering programs in law, business, finance, and health. Known for strong industry ties and career-oriented training.'
    },
    {
      name: 'Buckinghamshire New University',
      description: 'Offers a wide range of undergraduate and postgraduate programs with a strong focus on practical, career-oriented education.'
    },
    {
      name: 'Cardiff Metropolitan University',
      description: 'Renowned for applied research and practical education, especially in art, design, sport, and health sciences.'
    },
    {
      name: 'Glasgow Caledonian University',
      description: 'Excels in business, engineering, health sciences, and sustainable development with a focus on social innovation.'
    },
    {
      name: 'Leeds Beckett University',
      description: 'Strong in sports, business, and creative industries. Known for student life and employability focus.'
    },
    {
      name: 'Liverpool John Moores University',
      description: 'Research-driven university with strong programs in engineering, sports science, and maritime studies.'
    },
    {
      name: 'Anglia Ruskin University',
      description: 'Focuses on employability and innovation with wide-ranging courses and strong student support.'
    },
    {
      name: 'University of East London',
      description: 'Practical and diverse institution with urban-focused research and social justice initiatives.'
    },
    {
      name: 'University of Gloucestershire',
      description: 'Known for sustainability, employability, and programs in arts, business, and sciences.'
    },
    {
      name: 'De Montfort University',
      description: 'Known for innovation and strong creative arts, business, and engineering programs.'
    },
    {
      name: 'Arden University',
      description: 'Specializes in flexible, online learning. Focused on career-based programs and student support.'
    },
    {
      name: 'University of the West of Scotland',
      description: 'Renowned for research in health, business, and engineering, with community-focused learning.'
    },
    {
      name: 'University of Wolverhampton',
      description: 'Supports social mobility with programs in business, health, and engineering.'
    },
    {
      name: 'Wrexham Glyndwr University',
      description: 'Offers practical learning in creative, health, and technical disciplines.'
    },
    {
      name: 'University of Leicester',
      description: 'Pioneered genetic fingerprinting, strong in both teaching and research across disciplines.'
    }
  ];

  const benefits = [
    {
      icon: <FaGraduationCap className="w-8 h-8 text-primary" />,
      title: 'Seamless Transition',
      description: 'Top-up degree entry into year 2 or final year.'
    },
    {
      icon: <FaGlobe className="w-8 h-8 text-primary" />,
      title: 'Global Recognition',
      description: 'Qualifications respected by employers and universities worldwide.'
    },
    {
      icon: <FaRoute className="w-8 h-8 text-primary" />,
      title: 'Flexible Routes',
      description: 'Diplomas at Levels 4–7 aligned with university entry points.'
    },
    {
      icon: <FaUserGraduate className="w-8 h-8 text-primary" />,
      title: 'Expert Support',
      description: 'Dedicated guidance throughout your progression.'
    }
  ];

  return (
    <motion.div 
      className="min-h-screen bg-white"
      initial="hidden"
      animate="show"
      variants={container}
    >
      {/* Hero Section with Background Image */}
      <div 
        className="w-full h-[600px] relative overflow-hidden"
      >
        <img 
          src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
          alt="University Campus"
          className="w-full h-full object-cover object-center absolute inset-0"
        />
      </div>

      {/* Heading Section */}
      <div className="bg-white py-20 px-4">
        <motion.div 
          className="max-w-7xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            University Progression
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-gray-700">
            Empower your FIC College diploma with direct progression to top universities. Our pathway programs are tailored to help you continue into final-year degrees or postgraduate studies seamlessly.
          </p>
        </motion.div>
      </div>

      {/* Partner Universities List */}
      <motion.div className="py-16 bg-white" variants={fadeIn}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">List of Universities with Progression Paths</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of university partners and what they offer:
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {partnerUniversities.map((uni, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition duration-300 transform hover:-translate-y-1"
                variants={item}
              >
                <div className="flex items-center mb-4">
                  <FaUniversity className="text-primary mr-3" size={24} />
                  <h3 className="text-xl font-semibold text-gray-900">{uni.name}</h3>
                </div>
                <p className="text-gray-600">{uni.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div className="py-16 bg-gray-50" variants={fadeIn}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose FIC Progression?</h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transform hover:-translate-y-1"
                variants={item}
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UniversityProgression;
